import { z } from "zod";

const NumberMeta = z.object({
  type: z.literal("number"),
  fallback: z.number().optional(),
});

const StringMeta = z.object({
  type: z.literal("string"),
  fallback: z.string().optional(),
});

const BooleanMeta = z.object({
  type: z.literal("boolean"),
  fallback: z.boolean().optional(),
  invert: z.boolean().optional(),
  fuzzy: z.boolean().optional(),
});

const Meta = z.discriminatedUnion("type", [NumberMeta, StringMeta, BooleanMeta]);

export type DynamicValueMeta = z.infer<typeof Meta>;

const Reference = z.object({
  meta: Meta,
});

const ExternalValueRef = Reference.extend({
  type: z.literal("ext"),
  property: z.string(),
});

export type ExternalValueRef = z.infer<typeof ExternalValueRef>;

const NodePropertyRef = Reference.extend({
  type: z.literal("np"),
  nodeId: z.number(),
  property: z.string(),
});

export type NodePropertyRef = z.infer<typeof NodePropertyRef>;

const ValueReference = z.discriminatedUnion("type", [ExternalValueRef, NodePropertyRef]);

export type ValueReference = z.infer<typeof ValueReference>;

const DynamicValue = z
  .object({
    __dv: ValueReference,
  })
  .strict();

export type DynamicValue = z.infer<typeof DynamicValue>;

export const PrimitiveValueNullish = z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined()]);

export type PrimitiveValueNullish = z.infer<typeof PrimitiveValueNullish>;

export const PrimitiveValue = z.union([z.string(), z.number(), z.boolean()]);

export type PrimitiveValue = z.infer<typeof PrimitiveValue>;

export type StaticValue = PrimitiveValueNullish | { [name: string]: StaticValue } | StaticValue[];

const ObjectValue = z.lazy(() =>
  z
    .record(StaticValue)
    .refine((value) => !("__dv" in value), "Value looks like a dynamic value but failed to parse as one"),
);

export const StaticValue: z.ZodType<StaticValue> = z.union([
  ...PrimitiveValueNullish.options,
  DynamicValue,
  ObjectValue,
  z.lazy(() => StaticValue.array()),
]);

export const Value = z.union([DynamicValue, StaticValue]);

export type Value = z.infer<typeof Value>;

interface ValueNameToTypeMap {
  boolean: boolean;
  number: number;
  string: string;
}

export const PrimitiveValueName = z.union([z.literal("boolean"), z.literal("number"), z.literal("string")]);

export type PrimitiveValueName = z.infer<typeof PrimitiveValueName>;

type MetaType<T extends ValueType> = {
  meta: { type: ValueTypeToName<T>; fallback?: T } & (T extends boolean ? { invert?: boolean; fuzzy?: boolean } : {});
};

export type BindableValue<T extends ValueType> =
  | T
  | { __dv: Omit<NodePropertyRef, "meta"> & MetaType<T> }
  | {
      __dv: Omit<ExternalValueRef, "meta"> & MetaType<T>;
    };

export type ValueType = string | number | boolean;

export type ValueTypeToName<T> = T extends string
  ? "string"
  : T extends number
    ? "number"
    : T extends boolean
      ? "boolean"
      : never;

export type ValueNameToType<T> = T extends keyof ValueNameToTypeMap ? ValueNameToTypeMap[T] : never;
